import {
  collection,
  doc,
  DocumentData,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import type { Notification } from '../types';

export type { Notification } from '../types';

const PAGE_SIZE = 50;

const toDate = (value: unknown): Date => {
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? new Date(0) : date;
};

export const normalizeNotificationData = (
  id: string,
  data: DocumentData
): Notification => {
  return {
    id,
    userId: data.userId,
    title: data.title,
    body: data.body,
    type: data.type,
    priority: data.priority,
    read: data.read === true,
    createdAt: toDate(data.createdAt),
    readAt: data.readAt ? toDate(data.readAt) : undefined,
    expiresAt: toDate(data.expiresAt),
    sourceEventId: data.sourceEventId,
    actionUrl: data.actionUrl,
    relatedId: data.relatedId,
    metadata: data.metadata,
  };
};

const mapNotification = (
  snapshot: QueryDocumentSnapshot<DocumentData>
): Notification => normalizeNotificationData(snapshot.id, snapshot.data());

const call = async <TInput extends object, TOutput>(
  name: string,
  input: TInput
): Promise<TOutput> => {
  const callable = httpsCallable<TInput, TOutput>(functions, name);
  return (await callable(input)).data;
};

class NotificationsService {
  subscribe(
    userId: string,
    onData: (
      notifications: Notification[],
      cursor: QueryDocumentSnapshot<DocumentData> | null
    ) => void,
    onError: (error: Error) => void
  ): () => void {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE)
    );
    return onSnapshot(
      notificationsQuery,
      snapshot =>
        onData(
          snapshot.docs.map(mapNotification),
          snapshot.docs[snapshot.docs.length - 1] || null
        ),
      error => onError(error)
    );
  }

  subscribeUnreadCount(
    userId: string,
    onData: (count: number, exists: boolean) => void,
    onError: (error: Error) => void
  ): () => void {
    return onSnapshot(
      doc(db, 'notificationCounters', userId),
      snapshot =>
        onData(
          Math.max(0, Number(snapshot.data()?.unreadCount || 0)),
          snapshot.exists()
        ),
      error => onError(error)
    );
  }

  async loadMore(
    userId: string,
    cursor: QueryDocumentSnapshot<DocumentData>
  ): Promise<{
    notifications: Notification[];
    cursor: QueryDocumentSnapshot<DocumentData> | null;
  }> {
    const page = await getDocs(
      query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        startAfter(cursor),
        limit(PAGE_SIZE)
      )
    );
    return {
      notifications: page.docs.map(mapNotification),
      cursor: page.docs[page.docs.length - 1] || null,
    };
  }

  async markAsRead(notificationId: string): Promise<void> {
    await call('notifications-markNotificationRead', { notificationId });
  }

  async markAllAsRead(): Promise<void> {
    await call('notifications-markAllNotificationsRead', {});
  }

  async deleteNotification(notificationId: string): Promise<void> {
    await call('notifications-deleteNotification', { notificationId });
  }
}

export const notificationsService = new NotificationsService();
export type NotificationCursor = QueryDocumentSnapshot<DocumentData>;
